// reorder.js — Listen per Ziehen umsortieren (Zeiger-Ereignisse, funktioniert mit Touch).
//   makeReorderable(listEl, { item: '.row', handle: '[data-drag]', onDrop: (from, to) => … })
// Während des Ziehens folgt die gehobene Zeile dem Finger, die anderen weichen per Feder aus; losgelassen federt sie
// in ihre Lücke, erst dann wird umsortiert. Am Rand scrollt die Liste mit (schneller, je näher am Rand).
// Zuhörer für move/up hängen am FENSTER: ein Umsortieren im DOM würde sonst die Zeigererfassung lösen
// (Lehre aus der Hue-App, siehe CLAUDE.md des Smart-Home-Projekts).
import { reducedMotion } from '../core/dom.js';
import { spring, haptic, ticker } from '../motion/physics.js';

/** Zielindex aus der Mitte der gezogenen Zeile und den Mitten der anderen (rein, testbar). */
export function targetIndex(centers, from, y) {
  let to = from;
  for (let i = 0; i < centers.length; i++) {
    if (i === from) continue;
    if (i < from && y < centers[i]) { to = Math.min(to, i); }
    if (i > from && y > centers[i]) { to = Math.max(to, i); }
  }
  return to;
}

/** Element von Index a nach b verschieben (neues Array). */
export function moveItem(arr, a, b) {
  const out = arr.slice();
  const [x] = out.splice(a, 1);
  out.splice(b, 0, x);
  return out;
}

/** Mitscrollen am Rand: px/s nach Abstand zum Rand (0 außerhalb der Zone, bis max am Rand). */
export function edgeSpeed(y, top, bottom, zone = 56, max = 900) {
  if (y < top + zone) return -max * Math.min(1, (top + zone - y) / zone);
  if (y > bottom - zone) return max * Math.min(1, (y - (bottom - zone)) / zone);
  return 0;
}

const scrollParent = (el) => {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if ((o === 'auto' || o === 'scroll') && p.scrollHeight > p.clientHeight) return p;
  }
  return document.scrollingElement || document.documentElement;
};

export function makeReorderable(list, { item, handle, onDrop }) {
  let drag = null;
  const rows = () => [...list.querySelectorAll(item)];
  const springs = new WeakMap();
  const sp = (r) => {
    let s = springs.get(r);
    if (!s) { s = spring(0, { spring: 'spatial-fast', precision: 0.5, onUpdate: (y) => { r.style.translate = Math.abs(y) > 0.5 ? `0 ${y}px` : ''; } }); springs.set(r, s); }
    return s;
  };
  const scrollTop = (sc) => (sc === document.scrollingElement || sc === document.documentElement ? window.scrollY : sc.scrollTop);
  const offset = () => drag.lastY - drag.y0 + (scrollTop(drag.sc) - drag.s0);
  const layout = () => {
    const dy = offset();
    sp(drag.row).jump(dy);
    const centers = drag.rects.map((r) => r.top + r.height / 2);
    const to = targetIndex(centers, drag.from, centers[drag.from] + dy);
    if (to === drag.to) return;
    drag.to = to;
    haptic('tick');
    drag.all.forEach((r, i) => {
      if (r === drag.row) return;
      let shift = 0;
      if (drag.from < to && i > drag.from && i <= to) shift = -drag.h;
      if (drag.from > to && i < drag.from && i >= to) shift = drag.h;
      sp(r).to(shift);
    });
  };
  const down = (e) => {
    const h = e.target.closest(handle);
    if (!h || !list.contains(h) || (e.button !== undefined && e.button !== 0)) return;
    const row = h.closest(item);
    const all = rows();
    const from = all.indexOf(row);
    if (from < 0) return;
    e.preventDefault();
    const rects = all.map((r) => r.getBoundingClientRect());
    const sc = scrollParent(list);
    drag = { id: e.pointerId, row, all, from, to: from, y0: e.clientY, lastY: e.clientY, sc, s0: scrollTop(sc), rects, h: rects[from].height + parseFloat(getComputedStyle(list).rowGap || 0), vScroll: 0 };
    row.classList.add('is-dragging');
    list.classList.add('is-reordering');
    if (!reducedMotion()) {
      // Anheben: die Zeile wird etwas größer (der Schatten kommt aus .is-dragging)
      row.animate?.([{ scale: '1' }, { scale: '1.03' }], { duration: 160, easing: 'cubic-bezier(.2,0,0,1)', fill: 'forwards' });
    }
    haptic('tick');
    try { h.setPointerCapture(e.pointerId); } catch {}
    // Mitscrollen am Rand über den gemeinsamen Takt
    const d = drag;
    ticker().add((dt) => {
      if (drag !== d) return false;
      if (d.vScroll) {
        const by = d.vScroll * dt;
        if (d.sc === document.scrollingElement || d.sc === document.documentElement) window.scrollBy(0, by); else d.sc.scrollTop += by;
        layout();
      }
      return true;
    });
  };
  const move = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    e.preventDefault();
    drag.lastY = e.clientY;
    const win = drag.sc === document.scrollingElement || drag.sc === document.documentElement;
    const box = win ? { top: 0, bottom: innerHeight } : drag.sc.getBoundingClientRect();
    drag.vScroll = reducedMotion() ? 0 : edgeSpeed(e.clientY, box.top, box.bottom);
    layout();
  };
  const up = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const d = drag; drag = null;
    list.classList.remove('is-reordering');
    const finish = () => {
      for (const r of d.all) { springs.get(r)?.jump(0); r.style.translate = ''; r.getAnimations?.().forEach((a) => a.cancel()); }
      d.row.classList.remove('is-dragging');
      if (d.to !== d.from && onDrop) onDrop(d.from, d.to);
    };
    if (d.to !== d.from) haptic('confirm');
    if (reducedMotion() || e.type === 'pointercancel') { finish(); return; }
    // In die Lücke federn: Ziel = Summe der Höhen der übersprungenen Zeilen
    const gap = d.h - d.rects[d.from].height;
    let slot = 0;
    if (d.to > d.from) for (let i = d.from + 1; i <= d.to; i++) slot += d.rects[i].height + gap;
    if (d.to < d.from) for (let i = d.to; i < d.from; i++) slot -= d.rects[i].height + gap;
    d.row.animate?.([{ scale: '1.03' }, { scale: '1' }], { duration: 200, easing: 'cubic-bezier(.2,0,0,1)', fill: 'forwards' });
    sp(d.row).to(slot).then(finish);
  };
  list.addEventListener('pointerdown', down);
  addEventListener('pointermove', move, { passive: false });
  addEventListener('pointerup', up);
  addEventListener('pointercancel', up);
  return () => {
    list.removeEventListener('pointerdown', down);
    removeEventListener('pointermove', move); removeEventListener('pointerup', up); removeEventListener('pointercancel', up);
  };
}
